import type { EarningsLedgerEntry } from '../schemas/agent.js';

/**
 * Pure ledger-summary helpers, extracted from the agent-pwa `/earnings`
 * + `/recruiter/earnings` routes (previously duplicated inline, untested)
 * so the money math has ONE tested home.
 *
 * MONEY ACCURACY: every amount is an integer count of sen (minor units),
 * signed — negative for clawbacks and payout debits. These helpers only
 * ever ADD integers; the single divide-by-100 to reach RM happens at the
 * render / CSV boundary, never here, so a float never accumulates.
 *
 * `kind: 'paid'` debit rows are EXCLUDED from earnings totals: a payout
 * moves already-earned money out — it is not negative earnings. This is
 * the same rule `summariseLedgerByMonth` applies (`if (cls === 'payout')
 * continue`) and matches how home.tsx sums cumulative earnings
 * (`entries.filter((e) => e.kind !== 'paid')`).
 */

export type LedgerRange = 'this_month' | 'last_month' | 'all_time';

export type LedgerStatus = 'all' | 'unpaid' | 'paid';

export interface LedgerTotals {
  /** Net earnings in sen: accruals + adjustments + clawbacks. Excludes payout debits. */
  readonly net: number;
  /** Accrued-only earnings in sen (kind === 'accrued'). */
  readonly accrued: number;
}

/**
 * Fold ledger entries into net + accrued sen totals. Payout debit rows
 * (kind === 'paid') are skipped so a settled payout never reads as a drop
 * in earnings. Empty input yields a GENUINE zero (net 0, accrued 0) —
 * callers must distinguish that from a failed read upstream and never
 * render a false RM 0.00 for a dropped listener (per no-dead-ends).
 */
export function summariseLedgerTotals(entries: readonly EarningsLedgerEntry[]): LedgerTotals {
  let net = 0;
  let accrued = 0;
  for (const e of entries) {
    if (e.kind === 'paid') continue; // payout debit — moves earned money out, not earnings
    net += e.amount;
    if (e.kind === 'accrued') accrued += e.amount;
  }
  return { net, accrued };
}

/**
 * Total already DISBURSED, as a POSITIVE sen figure: the sum of the
 * absolute value of every `kind: 'paid'` debit row `markPayoutPaid`
 * wrote (each stored NEGATIVE — see functions/src/agents/payouts.ts).
 * A never-paid / empty ledger yields a genuine 0.
 */
export function paidOutSen(entries: readonly EarningsLedgerEntry[]): number {
  let paid = 0;
  for (const e of entries) {
    if (e.kind === 'paid') paid += Math.abs(e.amount);
  }
  return paid;
}

/**
 * Money STILL OWED to the recipient in sen: lifetime earnings MINUS
 * everything already paid out. This is DISTINCT from
 * `summariseLedgerTotals().net` (lifetime earnings, which EXCLUDES
 * payout debits): `net` answers "how much have I ever earned",
 * `unpaidBalanceSen` answers "how much is still coming to me".
 *
 * Because a settled payout writes a NEGATIVE `kind: 'paid'` debit,
 * summing EVERY entry — INCLUDING those debits — yields exactly
 * `lifetimeEarnings − paidOut`:
 *
 *   sum(all) = sum(non-paid)    + sum(paid debits)
 *            = lifetimeEarnings + (−paidOut)
 *            = lifetimeEarnings − paidOut
 *
 * Clawbacks (negative, non-paid) correctly pull BOTH lifetime and
 * unpaid down. Floored at 0 so an over-paid / post-payout-clawback
 * edge never renders a negative "owed" figure to the recipient.
 * Empty ledger → genuine 0.
 *
 * BUG HISTORY (W5): the recruiter dashboard previously summed only the
 * NON-`paid` rows for its "unpaid balance", which equals LIFETIME
 * earnings — it never subtracted disbursed money, so a recruiter with a
 * settled payout saw their unpaid balance OVER-REPORTED by the paid-out
 * amount. This helper is the single tested home for the correct math.
 */
export function unpaidBalanceSen(entries: readonly EarningsLedgerEntry[]): number {
  let balance = 0;
  for (const e of entries) balance += e.amount; // includes the negative 'paid' debits
  return Math.max(0, balance);
}

/**
 * DISPLAY-side mirror of `computePayoutBatch`'s eligibility (see
 * `packages/shared/src/agents/payouts.ts`): the raw sum of `amount` in sen
 * over exactly the entries the monthly payout job would sweep — but
 * UN-thresholded and floored at 0, because a super-admin viewing an agent
 * wants "RM 70, rolling over" rather than the nothing `computePayoutBatch`
 * returns below the threshold.
 *
 * An entry is payout-ELIGIBLE iff ALL of (kept in lock-step with
 * `computePayoutBatch` — change both together):
 *   1. `!payoutId` — not already swept into an earlier payout batch.
 *   2. `kind !== 'paid'` — payout debit rows are money already moved out;
 *      never re-paid. (So accrued + adjustment + clawed_back all count.)
 *   3. `accruedAt` is present (non-zero) and `<= nowMs` — an entry that
 *      hasn't accrued yet, or carries no timestamp, isn't payable.
 *
 * The sum INCLUDES `clawed_back` negatives, so a RM 100 accrual plus a
 * RM 30 clawback nets RM 70 payable. `Math.max(0, …)` floors a
 * clawback-heavy net at 0 ("nothing payable, rolls over") so the stat
 * never shows a negative owed figure. Integer sen only — the single
 * divide-by-100 stays at the render boundary, never here.
 *
 * BUG HISTORY: the super-admin agent-detail view previously computed this
 * inline as `kind === 'accrued' || kind === 'adjustment'` (+ unswept),
 * which DROPPED `clawed_back` reversals — an unswept clawback failed to
 * reduce the displayed unpaid balance, OVER-REPORTING what was owed.
 * Mirroring `computePayoutBatch` here makes the displayed "Unpaid balance"
 * match what the payout job will actually pay against.
 */
export function payableBalanceSen(entries: readonly EarningsLedgerEntry[], nowMs: number): number {
  let balance = 0;
  for (const e of entries) {
    if (e.payoutId) continue;
    if (e.kind === 'paid') continue;
    const ms = tsMs(e.accruedAt);
    if (ms === 0 || ms > nowMs) continue;
    balance += e.amount; // signed — clawback negatives correctly pull the balance down
  }
  return Math.max(0, balance);
}

/** True when `ms` (epoch ms) falls inside `range` relative to `nowMs`. */
export function rangeContainsMs(ms: number, range: LedgerRange, nowMs: number): boolean {
  if (range === 'all_time') return true;
  const now = new Date(nowMs);
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  if (range === 'this_month') return ms >= monthStart;
  // last_month
  const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1).getTime();
  const lastMonthEnd = monthStart - 1;
  return ms >= lastMonthStart && ms <= lastMonthEnd;
}

/** Filter ledger entries to those whose `accruedAt` falls inside `range`. */
export function filterLedgerByRange(
  entries: readonly EarningsLedgerEntry[],
  range: LedgerRange,
  nowMs: number,
): EarningsLedgerEntry[] {
  if (range === 'all_time') return [...entries];
  return entries.filter((e) => rangeContainsMs(tsMs(e.accruedAt), range, nowMs));
}

/**
 * Filter by payout status, derived from `payoutId` presence:
 *   - 'unpaid' → entries not yet swept into a payout (no payoutId)
 *   - 'paid'   → entries swept into a payout (payoutId set)
 *
 * The `/earnings` route doesn't load the payout docs, so this treats
 * "swept into a payout" as "paid out" — the cheap resolution the plan
 * allows. The `/payouts` route shows each batch's precise status
 * (pending / processing / paid / failed).
 */
export function filterLedgerByStatus(
  entries: readonly EarningsLedgerEntry[],
  status: LedgerStatus,
): EarningsLedgerEntry[] {
  if (status === 'all') return [...entries];
  return entries.filter((e) => {
    const swept = typeof e.payoutId === 'string' && e.payoutId.length > 0;
    return status === 'paid' ? swept : !swept;
  });
}

/**
 * Duck-typed Firestore-Timestamp → epoch ms. Accepts either a real
 * `Timestamp` (has `toMillis`) or the `{ seconds, nanoseconds }` shape a
 * cache read can surface. Returns 0 for anything unrecognised.
 */
function tsMs(ts: unknown): number {
  if (!ts || typeof ts !== 'object') return 0;
  const obj = ts as { toMillis?: () => number; seconds?: number; nanoseconds?: number };
  if (typeof obj.toMillis === 'function') return obj.toMillis();
  if (typeof obj.seconds === 'number') {
    return obj.seconds * 1000 + Math.floor((obj.nanoseconds ?? 0) / 1_000_000);
  }
  return 0;
}
